import React, { useState, useEffect } from 'react';
import { Globe, Satellite, Sun, Moon, MonitorCog, Brain, LogOut } from 'lucide-react';
import { useApp } from '../contexts/AppContext';

const STATUS_DOT = {
  online:     { color: 'var(--color-green)', pulse: true },
  offline:    { color: '#ff2d55',             pulse: false },
  connecting: { color: '#ffd60a',             pulse: true },
  waking:     { color: '#ffd60a',             pulse: true },
};

const Header = ({ page, supaStatus = 'connecting', hfStatus = 'waking' }) => {
  const { theme, themeMode, toggleTheme, lang, toggleLang, t } = useApp();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const pageLabels = {
    'command-center': t('page_beranda'),
    'neovision':      t('page_vision'),
    'neointel':       t('page_intel'),
    'datacenter':     t('page_datacenter'),
  };

  const timeStr = now.toLocaleTimeString(lang === 'id' ? 'id-ID' : 'en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = now.toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <header
      className="flex items-center gap-4 px-4 shrink-0"
      style={{ height: 64, background: 'var(--color-surface)', borderBottom: '1px solid var(--border-dim)' }}
    >
      {/* Page breadcrumb */}
      <div className="flex items-center gap-2 min-w-0">
        <p className="text-sm font-black uppercase tracking-wider truncate font-mono" style={{ color: 'var(--text-hi)' }}>
          {pageLabels[page] || ''}
        </p>
      </div>

      <div className="flex-1" />

      <div className="hidden md:flex items-center gap-4">
        {/* Supabase realtime status */}
        <div className="flex items-center gap-1.5"
          title={
            lang === 'id'
              ? (supaStatus === 'online' ? 'Basis Data: Terhubung' : supaStatus === 'offline' ? 'Basis Data: Terputus' : 'Basis Data: Menghubungkan...')
              : (supaStatus === 'online' ? 'Database: Connected' : supaStatus === 'offline' ? 'Database: Disconnected' : 'Database: Connecting...')
          }>
          <div className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[supaStatus]?.pulse ? 'animate-blink' : ''}`}
            style={{ background: STATUS_DOT[supaStatus]?.color || '#ffd60a' }} />
          <Satellite size={11} color={STATUS_DOT[supaStatus]?.color || '#ffd60a'} />
          <span className="text-[9px] font-mono font-bold hidden md:inline"
            style={{ color: STATUS_DOT[supaStatus]?.color || '#ffd60a' }}>DB</span>
        </div>

        {/* HF Space (AI engine) status */}
        <div className="flex items-center gap-1.5"
          title={
            lang === 'id'
              ? (hfStatus === 'online' ? 'AI: AKTIF' : hfStatus === 'offline' ? 'AI: MATI' : 'AI: PEMANASAN')
              : (hfStatus === 'online' ? 'AI: ONLINE' : hfStatus === 'offline' ? 'AI: OFFLINE' : 'AI: WARMING UP')
          }>
          <div className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[hfStatus]?.pulse ? 'animate-blink' : ''}`}
            style={{ background: STATUS_DOT[hfStatus]?.color || '#ffd60a' }} />
          <Brain size={11} color={STATUS_DOT[hfStatus]?.color || '#ffd60a'} />
          <span className="text-[9px] font-mono font-bold hidden md:inline"
            style={{ color: STATUS_DOT[hfStatus]?.color || '#ffd60a' }}>AI</span>
        </div>

        <div style={{ width: 1, height: 16, background: 'var(--border-dim)' }} />
        <div className="text-right">
          <p className="text-sm font-black font-mono leading-none" style={{ color: 'var(--color-cyan)' }}>{timeStr}</p>
          <p className="text-[9px] leading-none mt-0.5" style={{ color: 'var(--text-mute)' }}>{dateStr}</p>
        </div>
        <div style={{ width: 1, height: 16, background: 'var(--border-dim)' }} />
      </div>


      {/* Theme toggle: SYSTEM → LIGHT → DARK → SYSTEM.
          Default = SYSTEM (mengikuti OS prefers-color-scheme atau jam sistem).
          User toggle persist explicit pref ke localStorage. */}
      <button
        onClick={toggleTheme}
        className="hidden md:flex items-center justify-center gap-1.5 rounded-lg transition-all hover:bg-white/5"
        style={{ height: 36, padding: '0 10px', border: '1px solid var(--border-dim)' }}
        title={
          lang === 'id'
            ? (themeMode === 'system'
                ? `Mode sistem (mengikuti OS/jam — sekarang ${theme === 'dark' ? 'gelap' : 'terang'}). Klik untuk pilih manual.`
                : themeMode === 'light' ? 'Mode terang. Klik untuk gelap.' : 'Mode gelap. Klik untuk sistem.')
            : (themeMode === 'system'
                ? `System mode (follows OS/clock — currently ${theme}). Click for manual.`
                : themeMode === 'light' ? 'Light mode. Click for dark.' : 'Dark mode. Click for system.')
        }
      >
        {themeMode === 'system'
          ? <MonitorCog size={14} color="var(--color-cyan)" />
          : themeMode === 'light'
            ? <Sun size={14} color="var(--text-dim)" />
            : <Moon size={14} color="var(--text-dim)" />}
        <span className="text-[9px] font-bold font-mono uppercase tracking-widest"
          style={{ color: themeMode === 'system' ? 'var(--color-cyan)' : 'var(--text-dim)' }}>
          {themeMode === 'system'
            ? (lang === 'id' ? 'SISTEM' : 'SYSTEM')
            : themeMode === 'light' ? (lang === 'id' ? 'TERANG' : 'LIGHT') : (lang === 'id' ? 'GELAP' : 'DARK')}
        </span>
      </button>

      {/* Language toggle */}
      <button
        onClick={toggleLang}
        className="hidden md:flex items-center gap-1.5 px-3 rounded-lg transition-all hover:bg-white/5"
        style={{ height: 36, border: '1px solid var(--border-dim)' }}
        title={lang === 'id' ? 'Switch to English' : 'Ganti ke Bahasa Indonesia'}
      >
        <Globe size={11} color="var(--text-dim)" />
        <span className="text-[10px] font-bold" style={{ color: 'var(--text-dim)' }}>
          {lang.toUpperCase()}
        </span>
      </button>

      {/* Keluar: hapus cookie sesi di server dan semua token sesi, lalu muat ulang; SecurityGate global minta login lagi */}
      <button
        onClick={() => {
          fetch('/api/auth-check', { method: 'DELETE' }).catch(() => {}).finally(() => {
            try { sessionStorage.clear(); } catch { /* noop */ }
            window.location.reload();
          });
        }}
        className="flex items-center gap-1.5 px-3 rounded-lg transition-all hover:bg-white/5"
        style={{ height: 36, border: '1px solid var(--border-dim)' }}
        title={lang === 'id' ? 'Keluar' : 'Log out'}
      >
        <LogOut size={11} color="var(--text-dim)" />
        <span className="text-[10px] font-bold" style={{ color: 'var(--text-dim)' }}>
          {lang === 'id' ? 'KELUAR' : 'LOGOUT'}
        </span>
      </button>
    </header>
  );
};

export default Header;
